import React, { useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { ResultContextInterface } from './ResultContext.types';
import { setDeepState } from './helpers/setDeepState';

/*
ResultContext
  Stores result data from creation endpoint
*/
const noop = () => {};
const DEFAULTS = {
  result: null,
  resultInitial: null,
  setNew: noop,
  resetResult: noop,
  setResultValue: noop,
  isEdited: () => false,
} as const;

export const ResultContext = React.createContext<ResultContextInterface>(DEFAULTS);

export const useResultContext = () => {
  const context = React.useContext(ResultContext);
  if (!context) throw new Error(`outside of provider`);
  return context;
};

export const ResultProvider = ({ children }: { children: React.ReactElement }) => {
  const providerStateRef = useRef<ResultContextInterface>(DEFAULTS);
  let setProviderStateFn: React.Dispatch<React.SetStateAction<ResultContextInterface>>;

  const providerActions: Partial<ResultContextInterface> = {
    setNew: (newResult) => {
      setDeepState(setProviderStateFn, ['result'], newResult);
      setDeepState(setProviderStateFn, ['resultInitial'], newResult);
    },
    resetResult: () => {
      setDeepState(setProviderStateFn, ['result'], null);
      setDeepState(setProviderStateFn, ['resultInitial'], null);
    },
    setResultValue: (attributePath, newValue) =>
      setDeepState(setProviderStateFn, ['result', ...attributePath], newValue),
    isEdited: () =>
      !isEqual(providerStateRef.current.result, providerStateRef.current.resultInitial),
  };

  const initialState = Object.assign({}, DEFAULTS, providerActions);
  const [providerState, setProviderState] = React.useState<ResultContextInterface>(initialState);
  providerStateRef.current = providerState;
  setProviderStateFn = setProviderState;

  return <ResultContext.Provider value={providerState}>{children}</ResultContext.Provider>;
};
